import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { API, formatGBP } from "@/lib/brand";
import { useAuth } from "@/context/AuthContext";
import {
  Sparkle,
  FloppyDisk,
  Lock,
  PencilSimple,
  CheckCircle,
  LockKey,
  Trash,
  ArrowRight,
} from "@phosphor-icons/react";
import MoneyInput from "@/components/MoneyInput";
import InfoTooltip from "@/components/InfoTooltip";
import WeeklyCheckIn from "@/components/WeeklyCheckIn";
import AIDisclaimer from "@/components/AIDisclaimer";
import AIInsightFootnote from "@/components/AIInsightFootnote";

// ---------- Pure helpers ----------
function buildForecastCashChain(forecast) {
  if (!forecast?.months) return [];
  const opening = forecast?.inputs?.opening_cash || 0;
  let running = opening;
  return forecast.months.map((m) => {
    const open = running;
    running += m.net || 0;
    return { open, close: running };
  });
}

function buildActualsCashChain(forecast, actualsByMonth) {
  if (!forecast?.months) return [];
  const opening = forecast?.inputs?.opening_cash || 0;
  let running = opening;
  return forecast.months.map((m) => {
    const a = actualsByMonth[m.index];
    const net = a
      ? (a.actual_income || 0) - (a.actual_costs || 0) - (a.actual_personal_draw || 0)
      : m.net || 0;
    const open = running;
    running += net;
    return { open, close: running, hasActual: !!a, net };
  });
}

// Build the seed list of income lines for live entry of month i (defaults to forecast).
function seedIncomeLines(forecast, i) {
  if (!forecast) return [];
  const m = forecast.months[i];
  if (!m) return [];
  return forecast.inputs.revenue_streams.map((rs) => {
    const fLine = (m.income_lines || []).find((l) => l.id === rs.id);
    return { id: rs.id, name: rs.name || "Offer", amount: Number(fLine?.amount || 0) };
  });
}

function seedCostLines(forecast, i) {
  if (!forecast) return [];
  const m = forecast.months[i];
  if (!m) return [];
  // Walk forecast cost_lines (which already filters out months where the cost doesn't land)
  return (m.cost_lines || []).map((c) => ({
    id: c.id,
    name: c.name,
    amount: Number(c.amount || 0),
  }));
}

// Iter40 — a month is only unlockable once the CALENDAR month has ended.
// Actuals for August unlock at 00:00 on 1 September. Any month whose 1st of
// the *following* month is after today is still locked. Returns { unlocked,
// unlockDate, unlockLabel }.
function monthUnlockState(label) {
  // label examples: "Aug 2026", "Feb 2027"
  if (!label) return { unlocked: false, unlockLabel: "" };
  const [mon, yrStr] = label.split(" ");
  const monIdx = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].indexOf(mon);
  const yr = Number(yrStr);
  if (monIdx < 0 || !yr) return { unlocked: true, unlockLabel: "" };
  const unlockDate = new Date(yr, monIdx + 1, 1); // 1st of following month, local time
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const unlocked = today >= unlockDate;
  const unlockLabel = unlockDate.toLocaleDateString("en-GB", {
    day: "numeric", month: "long", year: "numeric",
  });
  return { unlocked, unlockDate, unlockLabel };
}

// Compare entered actuals against the forecasted seed for the active month.
// Returns one delta entry per item that has changed (above a small noise threshold)
// AND each user-added line that has no matching forecast counterpart.
// Each delta carries enough context for the propagate wizard to patch / append
// to the inputs object correctly.
function computeDeltas(forecast, monthIdx, incomeLines, costLines, personalPay) {
  const out = [];
  if (!forecast) return out;
  const m = forecast.months?.[monthIdx];
  if (!m) return out;

  // Income deltas
  for (const line of incomeLines) {
    const fLine = (m.income_lines || []).find((l) => l.id === line.id);
    const a = Number(line.amount || 0);
    if (!fLine) {
      // Brand-new offer the user added in this month — surface it as an ADD.
      if (a > 0 && (line.name || "").trim()) {
        out.push({
          id: line.id,
          kind: "income_new",
          name: line.name,
          forecast: 0,
          actual: a,
          suggest: false, // adding a new revenue stream is a decision call
        });
      }
      continue;
    }
    const f = Number(fLine.amount || 0);
    if (Math.abs(a - f) >= 1) {
      out.push({
        id: line.id,
        kind: "income",
        name: line.name || fLine.name,
        forecast: f,
        actual: a,
        suggest: false,
      });
    }
  }

  for (const line of costLines) {
    const fLine = (m.cost_lines || []).find((l) => l.id === line.id);
    const a = Number(line.amount || 0);
    if (!fLine) {
      // Brand-new cost added during live entry.
      if (a > 0 && (line.name || "").trim()) {
        out.push({
          id: line.id,
          kind: "cost_new",
          name: line.name,
          forecast: 0,
          actual: a,
          suggest: true,
        });
      }
      continue;
    }
    const f = Number(fLine.amount || 0);
    if (Math.abs(a - f) >= 1) {
      out.push({
        id: line.id,
        kind: "cost",
        name: line.name || fLine.name,
        forecast: f,
        actual: a,
        suggest: true,
      });
    }
  }

  const fPay = Number(m.personal_draw || 0);
  const aPay = Number(personalPay || 0);
  if (Math.abs(aPay - fPay) >= 1) {
    out.push({
      id: "_pay",
      kind: "pay",
      name: "Personal pay",
      forecast: fPay,
      actual: aPay,
      suggest: false,
    });
  }

  return out;
}

export default function ActualsPage() {
  const { user } = useAuth();
  const isOneOff = user?.plan === "one_off";

  const [forecast, setForecast] = useState(null);
  const [actuals, setActuals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [monthIdx, setMonthIdx] = useState(0);
  const [incomeLines, setIncomeLines] = useState([]);
  const [costLines, setCostLines] = useState([]);
  const [personalPay, setPersonalPay] = useState(0);
  const [notes, setNotes] = useState("");
  // Iter40 — wins/challenges captured as part of the monthly ritual.
  const [wins, setWins] = useState("");
  const [challenges, setChallenges] = useState("");
  const [insight, setInsight] = useState("");
  const [insightSavedFlag, setInsightSavedFlag] = useState(false);
  const [nextSteps, setNextSteps] = useState([]);
  const [busy, setBusy] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  // Iter40 — the actuals card now defaults to a collapsed summary. This flag
  // flips true when the user taps "Log your actuals" / "Edit this month".
  const [expanded, setExpanded] = useState(false);
  // Iter40 — blocking "Thinking…" state while Save & Recompute runs (save +
  // reproject + AI insight). Kept separate from `busy` so the button copy
  // can swap and the form can dim.
  const [thinking, setThinking] = useState(false);
  // Propagate-to-rolling-forecast mini wizard state
  const [propagatePrompt, setPropagatePrompt] = useState(null);
  // Iter35b — two-tab IA. Default landing on the Weekly Check-in tab since
  // that's the higher-frequency touchpoint. Monthly Ritual tab hosts the
  // long-standing monthly-actuals flow that already lived here.
  const [activeTab, setActiveTab] = useState("weekly");
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [f, a] = await Promise.all([
        axios.get(`${API}/forecast`),
        isOneOff ? Promise.resolve({ data: [] }) : axios.get(`${API}/actuals`),
      ]);
      setForecast(f.data);
      setActuals(a.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Iter40 — pre-compute per-month unlock state so the tab row + column can
  // both react. `defaultUnlockedIdx` is the latest completed month; we use
  // it as the initial selected tab (falls back to 0 if nothing's unlocked yet).
  const monthUnlockStates = useMemo(() => {
    if (!forecast?.months) return [];
    return forecast.months.map((m) => monthUnlockState(m.label));
  }, [forecast]);
  const defaultUnlockedIdx = useMemo(() => {
    let last = -1;
    monthUnlockStates.forEach((s, i) => { if (s.unlocked) last = i; });
    return last >= 0 ? last : 0;
  }, [monthUnlockStates]);

  // Snap the initial month selection to the latest unlocked one so users
  // don't land on a locked (future) month by default.
  useEffect(() => {
    if (forecast?.months && defaultUnlockedIdx >= 0) {
      setMonthIdx((prev) => (prev === 0 && !monthUnlockStates[0]?.unlocked ? defaultUnlockedIdx : prev));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forecast, defaultUnlockedIdx]);

  const actualsByMonth = useMemo(() => {
    const map = {};
    for (const a of actuals) map[a.month_index] = a;
    return map;
  }, [actuals]);

  const activeMonth = useMemo(
    () => (forecast?.months ? forecast.months[monthIdx] : null),
    [forecast, monthIdx],
  );
  const existingActual = useMemo(() => actualsByMonth[monthIdx], [actualsByMonth, monthIdx]);

  const forecastChain = useMemo(() => buildForecastCashChain(forecast), [forecast]);
  const actualsChain = useMemo(
    () => buildActualsCashChain(forecast, actualsByMonth),
    [forecast, actualsByMonth],
  );

  // When month switches, hydrate live-entry state from existing actual or forecast seed.
  useEffect(() => {
    setUnlocked(false);
    // Iter40 — collapse the actuals card whenever the user switches months.
    setExpanded(false);
    if (existingActual) {
      // If the saved actual has line-item breakdowns, restore them; else fall back to seed.
      const incoming = Array.isArray(existingActual.actual_income_lines) && existingActual.actual_income_lines.length
        ? existingActual.actual_income_lines
        : seedIncomeLines(forecast, monthIdx);
      const outgoing = Array.isArray(existingActual.actual_cost_lines) && existingActual.actual_cost_lines.length
        ? existingActual.actual_cost_lines
        : seedCostLines(forecast, monthIdx);
      setIncomeLines(incoming.map((l) => ({ ...l })));
      setCostLines(outgoing.map((l) => ({ ...l })));
      setPersonalPay(existingActual.actual_personal_draw || 0);
      setNotes(existingActual.notes || "");
      setWins(existingActual.wins || "");
      setChallenges(existingActual.challenges || "");
      if (existingActual.ai_insight) {
        setInsight(existingActual.ai_insight);
        setNextSteps(Array.isArray(existingActual.ai_next_steps) ? existingActual.ai_next_steps : []);
        setInsightSavedFlag(true);
      } else {
        setInsight("");
        setNextSteps([]);
        setInsightSavedFlag(false);
      }
    } else {
      setIncomeLines(seedIncomeLines(forecast, monthIdx));
      setCostLines(seedCostLines(forecast, monthIdx));
      setPersonalPay(forecast?.months?.[monthIdx]?.personal_draw || 0);
      setNotes("");
      setWins("");
      setChallenges("");
      setInsight("");
      setNextSteps([]);
      setInsightSavedFlag(false);
    }
  }, [monthIdx, existingActual, forecast]);

  if (loading) return <div className="font-heading uppercase text-2xl">Loading…</div>;

  // Paywall: one-off / cancelled / none plans don't get the live entry & AI flow.
  const lockedActuals = ["one_off", "cancelled", "none"].includes(user?.plan);
  if (lockedActuals) {
    const isCancelled = user?.plan === "cancelled";
    return (
      <div className="space-y-8">
        <div>
          <div className="section-eyebrow">Monthly ritual</div>
          <h1 className="font-heading uppercase text-4xl md:text-5xl tracking-wide mt-4 leading-[1.05]">
            What actually happened?
          </h1>
        </div>
        <div className="card-yellow flex flex-col gap-4" data-testid="actuals-paywall">
          <div className="flex items-center gap-2">
            <LockKey size={28} weight="duotone" />
            <span className="font-heading uppercase text-2xl">
              {isCancelled ? "Subscription paused" : "Subscription only"}
            </span>
          </div>
          {isCancelled ? (
            <>
              <p className="leading-relaxed">
                Your historic actuals and AI insights are still here, safe and read-only. Anything
                you logged while subscribed stays with you forever.
              </p>
              <p className="leading-relaxed">
                To pick the monthly ritual back up — logging new actuals, generating fresh AI
                insights, and watching your rolling forecast extend — reactivate your subscription.
              </p>
            </>
          ) : (
            <>
              <p className="leading-relaxed">
                Logging your actuals each month is the most powerful habit in this whole tool. It's
                how you spot a quiet patch coming, see where the money's really leaking, and learn
                the rhythm of <em>your</em> business — not the generic one. Without actuals, you're
                guessing. With them, you're forecasting like a CFO.
              </p>
              <p className="leading-relaxed">
                This page (and the AI insights that come with it) is part of the monthly
                subscription. Upgrade once you're ready to make this a habit.
              </p>
            </>
          )}
          <div>
            <Link to="/upgrade" className="btn-primary" data-testid="actuals-upgrade-btn">
              {isCancelled ? "Reactivate subscription" : "Upgrade my plan"}
            </Link>
          </div>
        </div>

        {/* Cancelled users keep read-only access to their historic months below */}
        {isCancelled && actuals.length > 0 && (
          <div className="card" data-testid="historic-actuals-readonly">
            <h3 className="font-heading uppercase text-xl">Your historic months</h3>
            <p className="text-sm text-black/65 mt-1">
              Read-only — every month you closed while subscribed, including its AI insight.
            </p>
            <div className="mt-4 space-y-3">
              {actuals
                .slice()
                .sort((a, b) => a.month_index - b.month_index)
                .map((a) => (
                  <div
                    key={a.id || `${a.month_index}`}
                    className="border-[1.5px] border-black/15 rounded-xl p-4"
                    data-testid={`historic-actual-${a.month_index}`}
                  >
                    <div className="flex items-baseline justify-between">
                      <div className="font-heading uppercase">{a.month_label}</div>
                      <div className="text-sm text-black/65">
                        Net{" "}
                        <strong>
                          {formatGBP(
                            (a.actual_income || 0) -
                              (a.actual_costs || 0) -
                              (a.actual_personal_draw || 0),
                          )}
                        </strong>
                      </div>
                    </div>
                    <div className="text-sm text-black/65 mt-1">
                      In {formatGBP(a.actual_income)} · Out{" "}
                      {formatGBP(a.actual_costs + (a.actual_personal_draw || 0))}
                    </div>
                    {a.ai_insight && (
                      <div className="ai-insight mt-3">
                        <span className="chip mb-2 inline-block">What this told you</span>
                        <p className="whitespace-pre-wrap leading-relaxed mt-1">{a.ai_insight}</p>
                        {Array.isArray(a.ai_next_steps) && a.ai_next_steps.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-black/15">
                            <div className="text-xs uppercase tracking-wider font-semibold text-black/70 mb-1.5">
                              Next steps
                            </div>
                            <ol className="list-decimal list-inside space-y-1 text-sm leading-relaxed">
                              {a.ai_next_steps.map((s, i) => (
                                <li key={i}>{s}</li>
                              ))}
                            </ol>
                          </div>
                        )}
                        <AIInsightFootnote className="mt-4" data-testid={`historic-insight-disclaimer-${a.month_index}`} />
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (!forecast) {
    return (
      <div className="card max-w-2xl">
        <h1 className="font-heading uppercase text-3xl">No forecast yet</h1>
        <Link to="/onboarding" className="btn-primary mt-6 inline-flex">
          Start building your forecast
        </Link>
      </div>
    );
  }

  const isClosed = !!existingActual && !unlocked;
  // Iter40 — derived flags for the rebuilt actuals column.
  //   `activeMonthLocked` = future month, can't log yet.
  //   `isEditing`        = user is in the expanded edit form. Also true when
  //                        an existing actual has been "unlocked" via the
  //                        legacy variance-regen path.
  const activeMonthLocked = !(monthUnlockStates[monthIdx]?.unlocked ?? true);
  const isEditing = expanded || unlocked;

  // Totals (live)
  const aIncomeTotal = incomeLines.reduce((s, l) => s + (Number(l.amount) || 0), 0);
  const aCostsTotal = costLines.reduce((s, l) => s + (Number(l.amount) || 0), 0);
  const aPay = Number(personalPay) || 0;
  const aNet = aIncomeTotal - aCostsTotal - aPay;
  const aOpen = monthIdx === 0
    ? forecast?.inputs?.opening_cash || 0
    : actualsChain[monthIdx - 1]?.close || 0;
  const aClose = aOpen + (existingActual ? actualsChain[monthIdx]?.net || 0 : aNet);

  // Forecast figures
  const fOpen = forecastChain[monthIdx]?.open || 0;
  const fClose = forecastChain[monthIdx]?.close || 0;
  const fIncome = activeMonth?.total_income || 0;
  const fCosts = activeMonth?.total_costs || 0;
  const fPay = activeMonth?.personal_draw || 0;
  const fNet = activeMonth?.net || 0;

  // Variance figures
  const varIncome = aIncomeTotal - fIncome;
  const varOut = aCostsTotal + aPay - (fCosts + fPay);
  const varNet = aNet - fNet;
  // Variance Health Score for the active month: actual_net vs forecast_net as a percentage.
  // (Mirrors backend dashboard logic but localised to one month.)
  const varHealthPct = (() => {
    if (!existingActual && aIncomeTotal === 0 && aCostsTotal === 0 && aPay === 0) return null;
    if (Math.abs(fNet) < 0.01) return null;
    return Math.round((varNet / Math.abs(fNet)) * 100);
  })();

  const save = async () => {
    setBusy(true);
    // Snapshot the forecast deltas BEFORE we save so we can offer to propagate them.
    const deltas = computeDeltas(forecast, monthIdx, incomeLines, costLines, personalPay);
    try {
      await axios.post(`${API}/actuals`, {
        forecast_id: forecast.id,
        month_index: monthIdx,
        actual_income: aIncomeTotal,
        actual_costs: aCostsTotal,
        actual_personal_draw: aPay,
        notes,
        actual_income_lines: incomeLines,
        actual_cost_lines: costLines,
        wins,
        challenges,
      });
      await axios.post(`${API}/forecast/reproject`);
      toast.success("Actuals saved. Rolling forecast refreshed.");
      setUnlocked(false);
      setExpanded(false);
      await load();
      // If anything diverged, open the mini wizard so the user can decide which
      // changes should also affect their forward-looking rolling forecast.
      if (deltas.length > 0) {
        setPropagatePrompt({
          deltas,
          // Default: pre-tick recurring/personal drift, leave one-offs and ad-hoc unticked.
          selected: deltas.reduce((acc, d) => {
            acc[d.id] = d.suggest;
            return acc;
          }, {}),
        });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save actuals");
    } finally {
      setBusy(false);
    }
  };

  // Iter40 — the primary CTA on the actuals card. Saves, reprojects, generates
  // the fresh variance insight, and auto-scrolls the user to the variance card
  // so they land straight on the newly-generated commentary. Everything runs
  // under a blocking "Thinking…" state so the button clearly signals progress.
  const saveAndRecompute = async () => {
    if (thinking) return;
    setThinking(true);
    // Snapshot deltas before we mutate (used to prompt propagation after we
    // finish generating the insight).
    const deltas = computeDeltas(forecast, monthIdx, incomeLines, costLines, personalPay);
    let savedOk = false;
    try {
      await axios.post(`${API}/actuals`, {
        forecast_id: forecast.id,
        month_index: monthIdx,
        actual_income: aIncomeTotal,
        actual_costs: aCostsTotal,
        actual_personal_draw: aPay,
        notes,
        actual_income_lines: incomeLines,
        actual_cost_lines: costLines,
        wins,
        challenges,
      });
      await axios.post(`${API}/forecast/reproject`);
      savedOk = true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save actuals");
      setThinking(false);
      return;
    }
    // Insight is best-effort — if it fails, the save still stands.
    try {
      const res = await axios.post(`${API}/ai/variance-insight`, {
        month_label: activeMonth.label,
        forecast_income: fIncome,
        actual_income: aIncomeTotal,
        forecast_costs: fCosts + fPay,
        actual_costs: aCostsTotal + aPay,
        user_note: notes,
      });
      setInsight(res.data.insight);
      setNextSteps(Array.isArray(res.data.next_steps) ? res.data.next_steps : []);
      setInsightSavedFlag(true);
    } catch {
      // Non-fatal — user can regenerate later from within the variance card.
    }
    setExpanded(false);
    setUnlocked(false);
    await load();
    setThinking(false);
    if (savedOk) toast.success("Saved & refreshed.");
    // Scroll to variance card so the user sees the fresh insight.
    setTimeout(() => {
      const el = document.querySelector('[data-testid="variance-card"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
    // Offer to propagate meaningful drifts into the rolling forecast.
    if (deltas.length > 0) {
      setPropagatePrompt({
        deltas,
        selected: deltas.reduce((acc, d) => {
          acc[d.id] = d.suggest;
          return acc;
        }, {}),
      });
    }
  };

  // Iter40 — cancel the edit state and revert local form back to whatever
  // the server has for this month.
  const cancelEdit = () => {
    setExpanded(false);
    setUnlocked(false);
    // Re-hydrate from existing actual OR forecast seed.
    if (existingActual) {
      const incoming = Array.isArray(existingActual.actual_income_lines) && existingActual.actual_income_lines.length
        ? existingActual.actual_income_lines
        : seedIncomeLines(forecast, monthIdx);
      const outgoing = Array.isArray(existingActual.actual_cost_lines) && existingActual.actual_cost_lines.length
        ? existingActual.actual_cost_lines
        : seedCostLines(forecast, monthIdx);
      setIncomeLines(incoming.map((l) => ({ ...l })));
      setCostLines(outgoing.map((l) => ({ ...l })));
      setPersonalPay(existingActual.actual_personal_draw || 0);
      setNotes(existingActual.notes || "");
      setWins(existingActual.wins || "");
      setChallenges(existingActual.challenges || "");
    } else {
      setIncomeLines(seedIncomeLines(forecast, monthIdx));
      setCostLines(seedCostLines(forecast, monthIdx));
      setPersonalPay(forecast?.months?.[monthIdx]?.personal_draw || 0);
      setNotes("");
      setWins("");
      setChallenges("");
    }
  };

  const applyPropagation = async () => {
    if (!propagatePrompt) return;
    const picked = propagatePrompt.deltas.filter((d) => propagatePrompt.selected[d.id]);
    if (picked.length === 0) {
      setPropagatePrompt(null);
      return;
    }
    setBusy(true);
    try {
      // Pull the latest inputs and patch only [monthIdx+1 .. 11] so historic
      // months keep their original figures.
      const fresh = await axios.get(`${API}/forecast`);
      const inputs = { ...fresh.data.inputs };
      const m = monthIdx; // index of the month the actuals were saved against

      // ---- Income (existing offers): preserve historic months, override future months
      inputs.revenue_streams = (inputs.revenue_streams || []).map((rs) => {
        const d = picked.find((p) => p.kind === "income" && p.id === rs.id);
        if (!d) return rs;
        if (rs.offer_type === "other" && Array.isArray(rs.monthly_amounts) && rs.monthly_amounts.length === 12) {
          const amounts = [...rs.monthly_amounts];
          for (let k = m + 1; k < 12; k++) amounts[k] = d.actual;
          return { ...rs, monthly_amounts: amounts };
        }
        const price = Number(rs.price) || 0;
        // The "new clients per month" figure that, multiplied by price, gives d.actual.
        // We avoid pre-rounding so the backend lands on exactly d.actual after its own
        // 2-dp rounding (this is what fixes the ±£1 drift the user reported).
        const newClients = price > 0 ? d.actual / price : (rs.clients_per_month || 0);
        // Build a 12-element monthly_sales array. For months [0..m] keep whatever was
        // originally forecasting (so historic months don't shift). For months [m+1..11]
        // use the new figure.
        const seedSales = Array.isArray(rs.monthly_sales) && rs.monthly_sales.length === 12
          ? [...rs.monthly_sales]
          : Array.from({ length: 12 }, (_, k) => {
              if (rs.is_future && k < (rs.launch_month_index || 0)) return 0;
              return Number(rs.clients_per_month) || 0;
            });
        for (let k = m + 1; k < 12; k++) seedSales[k] = newClients;
        return { ...rs, monthly_sales: seedSales };
      });

      // ---- Income (new offers): append a new revenue stream that sells from m+1 only
      for (const d of picked.filter((p) => p.kind === "income_new")) {
        const monthly_sales = Array(12).fill(0);
        for (let k = m + 1; k < 12; k++) monthly_sales[k] = 1;
        inputs.revenue_streams = [
          ...inputs.revenue_streams,
          {
            id: d.id,
            name: d.name,
            price: d.actual, // 1 sale × actual amount = exact figure
            clients_per_month: 0,
            offer_type: "service",
            is_future: true,
            launch_month_index: m + 1,
            monthly_sales,
            seasonality: null,
          },
        ];
      }

      // ---- Cost (existing): use monthly_amount_overrides so historic months keep their value
      inputs.recurring_costs = (inputs.recurring_costs || []).map((c) => {
        const d = picked.find((p) => p.kind === "cost" && p.id === c.id);
        if (!d) return c;
        const overrides = Array.isArray(c.monthly_amount_overrides) && c.monthly_amount_overrides.length === 12
          ? [...c.monthly_amount_overrides]
          : Array(12).fill(null);
        for (let k = m + 1; k < 12; k++) overrides[k] = d.actual;
        return { ...c, monthly_amount_overrides: overrides };
      });

      // ---- Cost (new): append a new monthly cost active from m+1 onwards
      for (const d of picked.filter((p) => p.kind === "cost_new")) {
        const months_active = [];
        for (let k = m + 1; k < 12; k++) months_active.push(k);
        inputs.recurring_costs = [
          ...inputs.recurring_costs,
          {
            id: d.id,
            name: d.name,
            amount: d.actual,
            kind: "monthly",
            frequency: "custom",
            anchor_month_index: 0,
            months_active,
          },
        ];
      }

      // ---- Personal pay drift: future-only via personal_draw_overrides
      const payDelta = picked.find((p) => p.kind === "pay");
      if (payDelta) {
        const seed = Array.isArray(inputs.personal_draw_overrides) && inputs.personal_draw_overrides.length === 12
          ? [...inputs.personal_draw_overrides]
          : Array(12).fill(Number(inputs.personal_draw) || 0);
        for (let k = m + 1; k < 12; k++) seed[k] = payDelta.actual;
        inputs.personal_draw_overrides = seed;
      }

      await axios.patch(`${API}/forecast/inputs`, { inputs });
      toast.success("Forecast updated. Take a look.");
      setPropagatePrompt(null);
      navigate("/forecast");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update forecast");
    } finally {
      setBusy(false);
    }
  };

  // "See how this shapes your Forecast Story" — kick off a regeneration
  // (the actuals save already $unset the cached story, so this is allowed)
  // and land the user on the Forecast page's story section.
  const regenerateAndGoToStory = async () => {
    setBusy(true);
    try {
      await axios.get(`${API}/ai/forecast-story?regenerate=true`);
      toast.success("Forecast Story refreshed with your latest actuals.");
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      // 429 = story already reflects latest data — that's fine, still show it
      if (status !== 429) {
        toast.message(detail || "Could not refresh the story right now, taking you to it anyway.");
      }
    } finally {
      setBusy(false);
      navigate("/forecast#forecast-story");
    }
  };

  const updateIncomeLine = (idx, val) =>
    setIncomeLines((arr) => arr.map((l, k) => (k === idx ? { ...l, amount: Number(val) || 0 } : l)));
  const updateIncomeLineName = (idx, name) =>
    setIncomeLines((arr) => arr.map((l, k) => (k === idx ? { ...l, name } : l)));
  const removeIncomeLine = (idx) =>
    setIncomeLines((arr) => arr.filter((_, k) => k !== idx));
  const addIncomeLine = () =>
    setIncomeLines((arr) => [
      ...arr,
      { id: `extra_inc_${Date.now()}`, name: "", amount: 0 },
    ]);

  const updateCostLine = (idx, val) =>
    setCostLines((arr) => arr.map((l, k) => (k === idx ? { ...l, amount: Number(val) || 0 } : l)));
  const updateCostLineName = (idx, name) =>
    setCostLines((arr) => arr.map((l, k) => (k === idx ? { ...l, name } : l)));
  const removeCostLine = (idx) =>
    setCostLines((arr) => arr.filter((_, k) => k !== idx));
  const addCostLine = (kind) =>
    setCostLines((arr) => [
      ...arr,
      {
        id: `extra_${kind}_${Date.now()}`,
        name: "",
        amount: 0,
        // Tag the kind so the propagate wizard can pre-select monthly costs by default.
        kind,
      },
    ]);

  return (
    <div className="space-y-6" data-testid="actuals-page">
      <div>
        <div className="section-eyebrow">Actuals</div>
        <h1 className="font-heading uppercase text-4xl md:text-5xl tracking-wide mt-4 leading-[1.05]">
          Your cash rhythm — weekly & monthly.
        </h1>
        <p className="mt-3 text-black/70 leading-relaxed">
          Two rhythms, one place. <strong>Weekly check-ins</strong> are quick Monday pulses — rough
          numbers, wins, and challenges that feed your Forecast Story with fresh context.{" "}
          <strong>Monthly Ritual</strong> is where you reconcile the full month, line by line, and
          compare against your forecast. Weekly is directional; monthly is exact. Together they
          make forecastly a weekly habit, not a monthly chore. And if you join the{" "}
          <Link to="/community" className="font-semibold underline">
            community live
          </Link>{" "}
          every week/every month, you will never have to figure this out alone.
        </p>
      </div>

      {/* Tab strip */}
      <div className="flex gap-1 border-b border-black/20" data-testid="actuals-tabs">
        {[
          { key: "weekly", label: "Weekly check-ins", testid: "tab-weekly" },
          { key: "monthly", label: "Monthly ritual", testid: "tab-monthly" },
        ].map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setActiveTab(t.key)}
            data-testid={t.testid}
            className={`px-5 py-3 font-heading uppercase text-sm tracking-wider border-[1.5px] border-b-0 rounded-t-lg transition-all ${
              activeTab === t.key
                ? "bg-[#FFBD4A] border-black text-black -mb-[1.5px]"
                : "bg-white border-black/20 text-black/60 hover:text-black hover:bg-[#FFF7E5]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === "weekly" && <WeeklyCheckIn />}

      {activeTab === "monthly" && (
      <div className="space-y-10">
      <div className="flex flex-wrap gap-2" data-testid="actuals-month-tabs">
        {forecast.months.map((m, i) => {
          const hasActual = actualsByMonth[i];
          const unlockState = monthUnlockStates[i] || { unlocked: true };
          const locked = !unlockState.unlocked;
          return (
            <button
              key={i}
              data-testid={`actuals-month-${i}`}
              onClick={() => { if (!locked) setMonthIdx(i); }}
              disabled={locked}
              title={locked ? `Available from ${unlockState.unlockLabel}` : ""}
              className={`px-4 py-2 border-[1.5px] rounded-xl font-heading uppercase text-sm tracking-wide transition-all flex items-center gap-1.5 ${
                locked
                  ? "border-black/25 bg-white/60 text-black/40 cursor-not-allowed"
                  : monthIdx === i
                  ? "bg-[#FFBD4A] text-black border-black"
                  : hasActual
                  ? "bg-black text-white border-black"
                  : "bg-white border-black"
              }`}
            >
              {m.label.split(" ")[0]}
              {locked && <Lock size={12} weight="bold" />}
              {!locked && hasActual && <CheckCircle size={14} weight="fill" />}
            </button>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Forecast column — condensed summary */}
        <div className="card" data-testid="forecast-column">
          <div className="flex items-center justify-between">
            <h3 className="font-heading uppercase text-xl">Forecast · {activeMonth.label}</h3>
            <span className="chip">Plan</span>
          </div>
          <SummaryRow label="Opening cash" value={formatGBP(fOpen)} />
          <SummaryRow label="Income" value={formatGBP(fIncome)} />
          <SummaryRow label="Business costs" value={`− ${formatGBP(fCosts)}`} />
          <SummaryRow label="Personal pay" value={`− ${formatGBP(fPay)}`} />
          <SummaryRow label="Net cash flow for the month" value={formatGBP(fNet)} bold />
          <SummaryRow label="Closing cash" value={formatGBP(fClose)} bold highlight />
        </div>

        {/* Actuals column — Iter40 rebuilt: locked / collapsed-summary /
            open-form. Defaults to a collapsed CTA card ("Log your actuals
            for [Month Year]") so the page reads lightly. Expand → line-by-
            line form + wins/challenges + Save & Recompute + Cancel. */}
        <div className="card" data-testid="actuals-column">
          <div className="flex items-center justify-between">
            <h3 className="font-heading uppercase text-xl">Actual · {activeMonth.label}</h3>
            {(() => {
              if (activeMonthLocked) {
                return (
                  <span className="chip flex items-center gap-1" data-testid="actuals-locked-chip">
                    <Lock size={12} weight="bold" /> Locked
                  </span>
                );
              }
              if (existingActual && !isEditing) {
                return (
                  <span className="chip flex items-center gap-1" data-testid="actuals-logged-chip">
                    <CheckCircle size={12} weight="fill" /> Logged
                  </span>
                );
              }
              if (isEditing) {
                return <span className="chip">Live entry</span>;
              }
              return <span className="chip">Ready to log</span>;
            })()}
          </div>

          {activeMonthLocked ? (
            // ------------------------------------------------------------------
            // Locked (future) month — greyed-out card with the unlock date.
            // ------------------------------------------------------------------
            <div className="mt-6 space-y-4" data-testid="actuals-locked-card">
              <div className="flex items-start gap-3 p-4 rounded-xl border-[1.5px] border-black/20 bg-black/[0.02]">
                <Lock size={22} weight="duotone" className="shrink-0 mt-0.5 text-black/60" />
                <div className="flex-1">
                  <div className="font-heading uppercase text-lg text-black/70">
                    Available from {monthUnlockStates[monthIdx]?.unlockLabel}
                  </div>
                  <p className="text-sm text-black/60 leading-relaxed mt-1">
                    We unlock each month's actuals on the 1st of the following month, once every
                    transaction has landed. In the meantime, use your Weekly Check-ins to keep
                    the Forecast Story warm.
                  </p>
                </div>
              </div>
            </div>
          ) : !isEditing ? (
            // ------------------------------------------------------------------
            // Collapsed summary — either no actual yet (CTA) or actual logged
            // (read-only summary + Edit).
            // ------------------------------------------------------------------
            existingActual ? (
              <>
                <SummaryRow label="Opening cash" value={formatGBP(aOpen)} />
                <SummaryRow label="Income" value={formatGBP(aIncomeTotal)} />
                <SummaryRow label="Business costs" value={`− ${formatGBP(aCostsTotal)}`} />
                <SummaryRow label="Personal pay" value={`− ${formatGBP(aPay)}`} />
                <SummaryRow label="Net cash flow for the month" value={formatGBP(aNet)} bold />
                <SummaryRow label="Closing cash" value={formatGBP(aClose)} bold highlight />
                {notes && (
                  <div className="mt-4 text-sm text-black/65">
                    <strong>Note:</strong> {notes}
                  </div>
                )}
                <div className="mt-4">
                  <button
                    className="btn-secondary"
                    onClick={() => setExpanded(true)}
                    disabled={busy || thinking}
                    data-testid="actuals-edit-btn"
                  >
                    <PencilSimple size={16} weight="bold" /> Edit this month
                  </button>
                </div>
              </>
            ) : (
              <div className="mt-6" data-testid="actuals-collapsed-cta">
                <p className="text-black/75 leading-relaxed">
                  Ready to reconcile what actually landed? We'll pre-fill this month with your
                  forecast — you just adjust what came in different, add a win or two, and hit
                  Save & Recompute. It takes about 3 minutes.
                </p>
                <button
                  className="btn-primary mt-4"
                  onClick={() => setExpanded(true)}
                  disabled={busy || thinking}
                  data-testid="actuals-log-btn"
                >
                  <PencilSimple size={16} weight="bold" /> Log your actuals for {activeMonth.label}
                </button>
              </div>
            )
          ) : (
            // ------------------------------------------------------------------
            // Expanded form — line-by-line entry + wins/challenges + Save/Cancel.
            // ------------------------------------------------------------------
            <div className={thinking ? "opacity-60 pointer-events-none" : ""}>
              <SummaryRow label="Opening cash" value={formatGBP(aOpen)} />

              {/* Pre-populated income line items */}
              <div className="mt-4">
                <div className="font-heading uppercase text-base mb-2">Money in (per offer)</div>
                <div className="space-y-2">
                  {incomeLines.length === 0 && (
                    <div className="text-xs text-black/55">No offers configured.</div>
                  )}
                  {incomeLines.map((l, k) => (
                    <div key={l.id || k} className="grid grid-cols-[1fr_140px_auto] gap-3 items-center">
                      <input
                        className="input !py-2"
                        value={l.name}
                        onChange={(e) => updateIncomeLineName(k, e.target.value)}
                        placeholder="Offer name"
                        data-testid={`actuals-income-line-name-${k}`}
                      />
                      <MoneyInput
                        value={l.amount}
                        onChange={(n) => updateIncomeLine(k, n)}
                        testid={`actuals-income-line-${k}`}
                      />
                      <button
                        type="button"
                        className="btn-secondary !px-3 !py-2"
                        onClick={() => removeIncomeLine(k)}
                        data-testid={`actuals-income-line-remove-${k}`}
                        title="Remove"
                      >
                        <Trash size={14} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="text-sm font-semibold underline mt-1"
                    onClick={addIncomeLine}
                    data-testid="actuals-add-income-line"
                  >
                    + Add offer
                  </button>
                </div>
              </div>

              {/* Pre-populated cost line items */}
              <div className="mt-5">
                <div className="font-heading uppercase text-base mb-2">Money out (per cost)</div>
                <div className="space-y-2">
                  {costLines.length === 0 && (
                    <div className="text-xs text-black/55">No costs land in this month.</div>
                  )}
                  {costLines.map((l, k) => (
                    <div key={l.id || k} className="grid grid-cols-[1fr_140px_auto] gap-3 items-center">
                      <input
                        className="input !py-2"
                        value={l.name}
                        onChange={(e) => updateCostLineName(k, e.target.value)}
                        placeholder="Cost name"
                        data-testid={`actuals-cost-line-name-${k}`}
                      />
                      <MoneyInput
                        value={l.amount}
                        onChange={(n) => updateCostLine(k, n)}
                        testid={`actuals-cost-line-${k}`}
                      />
                      <button
                        type="button"
                        className="btn-secondary !px-3 !py-2"
                        onClick={() => removeCostLine(k)}
                        data-testid={`actuals-cost-line-remove-${k}`}
                        title="Remove"
                      >
                        <Trash size={14} />
                      </button>
                    </div>
                  ))}
                  <div className="flex flex-wrap gap-4 text-sm">
                    <button
                      type="button"
                      className="font-semibold underline"
                      onClick={() => addCostLine("monthly")}
                      data-testid="actuals-add-monthly-cost"
                    >
                      + Add monthly cost
                    </button>
                    <button
                      type="button"
                      className="font-semibold underline"
                      onClick={() => addCostLine("one_off")}
                      data-testid="actuals-add-oneoff-cost"
                    >
                      + Add one-off cost
                    </button>
                  </div>
                  <div className="grid grid-cols-[1fr_140px_auto] gap-3 items-center pt-3 border-t border-black/10">
                    <span className="text-sm">Personal pay taken</span>
                    <MoneyInput
                      value={personalPay}
                      onChange={(n) => setPersonalPay(n)}
                      testid="actuals-draw"
                    />
                    <span />
                  </div>
                </div>
              </div>

              {/* Live totals matching the forecast typography */}
              <div className="mt-4">
                <SummaryRow label="Net cash flow for the month" value={formatGBP(aNet)} bold />
                <SummaryRow label="Closing cash" value={formatGBP(aClose)} bold highlight />
              </div>

              {/* Iter40 — Wins + Challenges. Optional but powerful — they feed
                  the Forecast Story's monthly texture. */}
              <div className="mt-5 grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold mb-1">
                    Wins of the month <span className="text-black/45">(optional)</span>
                  </label>
                  <textarea
                    className="input min-h-[80px]"
                    value={wins}
                    onChange={(e) => setWins(e.target.value.slice(0, 500))}
                    placeholder="e.g. Signed my biggest retainer client yet, shipped the SaaS beta…"
                    data-testid="actuals-wins"
                    maxLength={500}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1">
                    Challenges of the month <span className="text-black/45">(optional)</span>
                  </label>
                  <textarea
                    className="input min-h-[80px]"
                    value={challenges}
                    onChange={(e) => setChallenges(e.target.value.slice(0, 500))}
                    placeholder="e.g. Time-poor on marketing, one late payment hit cash flow…"
                    data-testid="actuals-challenges"
                    maxLength={500}
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-xs font-semibold mb-1">What else happened? (optional)</label>
                <textarea
                  className="input min-h-[70px]"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Anything the numbers don't capture…"
                  data-testid="actuals-notes"
                />
              </div>

              <div className="flex flex-wrap gap-3 mt-5">
                <button
                  className="btn-primary"
                  onClick={saveAndRecompute}
                  disabled={busy || thinking}
                  data-testid="save-actuals-btn"
                >
                  {thinking ? (
                    <>
                      <Sparkle size={18} weight="fill" className="animate-pulse" /> Thinking…
                    </>
                  ) : (
                    <>
                      <FloppyDisk size={18} weight="bold" /> Save & Recompute
                    </>
                  )}
                </button>
                <button
                  className="btn-secondary"
                  onClick={cancelEdit}
                  disabled={thinking}
                  data-testid="actuals-cancel-btn"
                >
                  Cancel
                </button>
              </div>
              {thinking && (
                <p className="text-xs text-black/55 mt-3" data-testid="actuals-thinking-hint">
                  Reading your numbers and drafting a fresh insight — just a moment.
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="card" data-testid="variance-card">
        <h3 className="font-heading uppercase text-xl">Variance · {activeMonth.label}</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
          <Variance
            label="Money in"
            diff={varIncome}
            kind="income"
            testid="var-income"
            tooltip={
              <>
                Money in variance = your actual income minus your forecasted income for this month.
                Positive (yellow) = you brought in more than planned; negative = less than planned.
              </>
            }
          />
          <Variance
            // For Money out we INVERT the sign so the user sees:
            //   forecast spending less than actual (over-budget) → negative red figure
            //   forecast spending more than actual (under-budget) → positive yellow figure
            label="Money out"
            diff={-varOut}
            kind="costs"
            testid="var-out"
            tooltip={
              <>
                Money out variance = your forecasted spending minus your actual spending.
                Positive (yellow) means you spent less than planned. Negative means you spent more.
              </>
            }
          />
          <Variance
            label="Net cash flow"
            diff={varNet}
            kind="net"
            testid="var-net"
            tooltip={
              <>
                Net variance = actual net cash flow (money in minus money out) versus forecast.
                Positive means your bottom line for the month was healthier than planned.
              </>
            }
          />
          <VarianceHealth pct={varHealthPct} testid="var-health-score" />
        </div>
        {insight && (
          <div className="ai-insight mt-6" data-testid="ai-insight-box">
            <div className="flex items-center gap-2 mb-2">
              <span className="chip">What this tells you</span>
              {insightSavedFlag && (
                <span className="text-xs text-black/55 flex items-center gap-1">
                  <CheckCircle size={12} weight="fill" /> Saved with this month
                </span>
              )}
            </div>
            <p className="whitespace-pre-wrap leading-relaxed">{insight}</p>
            {nextSteps.length > 0 && (
              <div className="mt-4 pt-4 border-t border-black/15" data-testid="ai-next-steps">
                <div className="text-xs uppercase tracking-wider font-semibold text-black/70 mb-2">
                  Next steps for this month
                </div>
                <ol className="list-decimal list-inside space-y-1.5 text-sm leading-relaxed">
                  {nextSteps.map((s, i) => (
                    <li key={i} data-testid={`ai-next-step-${i}`}>
                      {s}
                    </li>
                  ))}
                </ol>
              </div>
            )}
            <AIInsightFootnote className="mt-4" data-testid="actuals-insight-disclaimer" />
            <div className="mt-4 pt-4 border-t border-black/10 flex items-start gap-2 flex-wrap" data-testid="variance-story-cta">
              <div className="flex-1 min-w-[220px]">
                <div className="text-xs uppercase tracking-widest text-black/55 font-semibold">
                  Want the deeper read?
                </div>
                <p className="text-sm text-black/75 leading-relaxed mt-1">
                  This is the quick verdict on this month's variance. For the whole-picture take —
                  how these actuals reshape the year ahead — refresh your Forecast Story.
                </p>
              </div>
              <button
                type="button"
                onClick={regenerateAndGoToStory}
                disabled={busy}
                className="btn-primary shrink-0"
                data-testid="variance-open-story-btn"
              >
                {busy ? "Refreshing…" : "See how this shapes your Forecast Story"}{" "}
                <ArrowRight size={14} weight="bold" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Propagate-to-rolling-forecast mini wizard */}
      {propagatePrompt && (
        <div
          className="fixed inset-0 z-50 bg-black/55 flex items-center justify-center p-4"
          data-testid="propagate-modal"
        >
          <div className="bg-white border-[1.5px] border-black rounded-2xl shadow-xl max-w-xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="font-heading uppercase text-2xl">Quick check before you go</h3>
            <p className="mt-2 text-black/75 leading-relaxed">
              You've made some changes to the actual figures for{" "}
              <strong>{activeMonth.label}</strong>. Would you like to carry over some or all of
              them into your rolling 12-month forecast (next month onwards)? If so, tick the ones
              you'd like to carry over, so your forward view stays realistic. Leave anything that
              was a one-off only unticked.
            </p>
            <div className="mt-5 space-y-2">
              {propagatePrompt.deltas.map((d) => (
                <label
                  key={d.id}
                  className="flex items-start gap-3 border-[1.5px] border-black/15 rounded-lg p-3 cursor-pointer hover:bg-black/[0.02]"
                  data-testid={`propagate-row-${d.id}`}
                >
                  <input
                    type="checkbox"
                    className="w-4 h-4 mt-0.5"
                    checked={!!propagatePrompt.selected[d.id]}
                    onChange={(e) =>
                      setPropagatePrompt((p) => ({
                        ...p,
                        selected: { ...p.selected, [d.id]: e.target.checked },
                      }))
                    }
                    data-testid={`propagate-check-${d.id}`}
                  />
                  <div className="flex-1">
                    <div className="font-semibold">{d.name}</div>
                    <div className="text-sm text-black/65">
                      Forecast {formatGBP(d.forecast)} → Actual{" "}
                      <strong>{formatGBP(d.actual)}</strong>
                      {d.kind === "income" && " (per month)"}
                    </div>
                  </div>
                </label>
              ))}
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                className="btn-primary"
                onClick={applyPropagation}
                disabled={busy}
                data-testid="propagate-apply"
              >
                <ArrowRight size={16} weight="bold" /> Update forecast & view
              </button>
              <button
                className="btn-secondary"
                onClick={() => setPropagatePrompt(null)}
                disabled={busy}
                data-testid="propagate-skip"
              >
                Skip for now
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
      )}
    </div>
  );
}

// Consistent typography summary row — same font size for label + value.
// `bold` keeps Anton heading weight on the row (used for Net + Closing).
// `highlight` adds the cream wash on the Closing cash row.
function SummaryRow({ label, value, bold, highlight }) {
  return (
    <div
      className={`flex items-baseline justify-between border-b border-black/15 py-2 ${
        highlight ? "bg-[#FFF7E5] -mx-2 px-2 rounded-md" : ""
      }`}
    >
      <span className={bold ? "font-heading uppercase text-base" : "text-base text-black/75"}>
        {label}
      </span>
      <span className={bold ? "font-heading text-base" : "text-base"}>{value}</span>
    </div>
  );
}

function Variance({ label, diff, kind, testid, tooltip }) {
  // Treat tiny noise (rounding) as zero.
  const isZero = Math.abs(diff) < 0.005;
  const positive = diff > 0;
  // Per-box copy. `kind` lets us tailor wording for "Money out" specifically.
  let sub;
  if (isZero) {
    sub = "Spot on! Your actuals were the same as your forecasted figures.";
  } else if (kind === "costs") {
    sub = positive
      ? "Yay! Your actual costs were lower than your forecasted costs."
      : "Oh oh, your actual costs were higher than your forecasted costs.";
  } else {
    sub = positive
      ? "Yay! Your actuals were higher than your forecasted figures."
      : "Oh oh, your actuals were lower than your forecasted figures.";
  }
  return (
    <div
      className={`border-[1.5px] border-black rounded-xl p-4 ${
        isZero ? "bg-white" : positive ? "bg-[#FFBD4A]" : "bg-white"
      }`}
      data-testid={testid}
    >
      <div className="flex items-center gap-1 text-xs uppercase text-black/60">
        <span>{label}</span>
        {tooltip && <InfoTooltip label={label}>{tooltip}</InfoTooltip>}
      </div>
      <div className="font-heading text-2xl mt-1">{formatGBP(diff)}</div>
      <div className="text-xs mt-1 leading-snug">{sub}</div>
    </div>
  );
}

function VarianceHealth({ pct, testid }) {
  const isZero = pct !== null && pct === 0;
  const positive = pct !== null && pct > 0;
  const value = pct === null ? "—" : `${pct > 0 ? "+" : ""}${pct}%`;
  const sub =
    pct === null
      ? "Save your actuals to see this score."
      : isZero
      ? "Spot on! Your actuals were the same as your forecasted figures."
      : positive
      ? "You've done better than you forecasted this month. Well done. Keep going!"
      : "Ah this month didn't quite go as planned. That's okay. Check what can be done over on the Actuals page insights.";
  return (
    <div
      className={`border-[1.5px] border-black rounded-xl p-4 ${
        positive ? "bg-[#FFBD4A]" : "bg-white"
      }`}
      data-testid={testid}
    >
      <div className="flex items-center gap-1 text-xs uppercase text-black/60">
        <span>Variance health score</span>
        <InfoTooltip label="Variance health score">
          One tidy % that summarises how close your actual net cash flow landed to your forecasted net for
          this month. +5% = you beat your plan by 5%. −20% = you undershot by 20%. Consistent small
          variances are the goal — big swings signal something worth investigating.
        </InfoTooltip>
      </div>
      <div className="font-heading text-2xl mt-1">{value}</div>
      <div className="text-xs mt-1 leading-snug">{sub}</div>
    </div>
  );
}
